import parse from 'date-fns/parse';
import Fluxxor from 'fluxxor';
import formatDate from 'utils/formatDate';

import {
  STATUS_ERROR,
  STATUS_INITIAL,
  STATUS_NOT_FOUND,
  STATUS_PENDING,
  STATUS_SUCCESS,
} from '../../constants';

export const constants = {
  FETCH_POPULAR_EXPERIENCES_SUCCESS: 'FETCH_POPULAR_EXPERIENCES_SUCCESS',
  FETCH_EXPERIENCE: 'FETCH_EXPERIENCE',
  FETCH_EXPERIENCE_SUCCESS: 'FETCH_EXPERIENCE_SUCCESS',
  FETCH_EXPERIENCE_REDIRECT: 'FETCH_EXPERIENCE_REDIRECT',
  FETCH_EXPERIENCE_NOT_FOUND: 'FETCH_EXPERIENCE_NOT_FOUND',
  FETCH_AVAILABLE_TIMES: 'FETCH_AVAILABLE_TIMES',
  FETCH_AVAILABLE_TIMES_SUCCESS: 'FETCH_AVAILABLE_TIMES_SUCCESS',
  FETCH_AVAILABLE_DATES: 'FETCH_AVAILABLE_DATES',
  FETCH_AVAILABLE_DATES_SUCCESS: 'FETCH_AVAILABLE_DATES_SUCCESS',
  FETCH_AVAILABLE_DATES_PER_HOST: 'FETCH_AVAILABLE_DATES_PER_HOST',
  FETCH_AVAILABLE_DATES_PER_HOST_SUCCESS:
    'FETCH_AVAILABLE_DATES_PER_HOST_SUCCESS',
  FETCH_SUGGESTIONS: 'FETCH_SUGGESTIONS',
  FETCH_SUGGESTIONS_SUCCESS: 'FETCH_SUGGESTIONS_SUCCESS',
  FETCH_SUGGESTIONS_FAIL: 'FETCH_SUGGESTIONS_FAIL',
};

export const ExperienceStore = Fluxxor.createStore({
  initialize() {
    this.experience = {
      id: null,
      status: STATUS_INITIAL,
      data: null,
    };

    this.redirectUrl = null;

    this.availableTimes = {
      experienceId: null,
    };

    this.availableDates = {
      experienceId: null,
      status: STATUS_PENDING,
      data: null,
    };

    this.availableDatesPerHost = {
      experienceId: null,
      status: STATUS_PENDING,
      data: null,
    };

    this.popularExperiences = [];
    this.suggestions = {};
    this.defaultTime = null;

    this.bindActions(
      constants.FETCH_POPULAR_EXPERIENCES_SUCCESS,
      this.onFetchPopularExperiencesSuccess,
      constants.FETCH_EXPERIENCE,
      this.onFetchExperience,
      constants.FETCH_EXPERIENCE_SUCCESS,
      this.onFetchExperienceSuccess,
      constants.FETCH_EXPERIENCE_REDIRECT,
      this.onFetchExperienceRedirect,
      constants.FETCH_EXPERIENCE_NOT_FOUND,
      this.onFetchExperienceNotFound,
      constants.FETCH_AVAILABLE_TIMES,
      this.onFetchAvailableTimes,
      constants.FETCH_AVAILABLE_TIMES_SUCCESS,
      this.onFetchAvailableTimesSuccess,
      constants.FETCH_AVAILABLE_DATES,
      this.onFetchAvailableDates,
      constants.FETCH_AVAILABLE_DATES_SUCCESS,
      this.onFetchAvailableDatesSuccess,
      constants.FETCH_AVAILABLE_DATES_PER_HOST,
      this.onFetchAvailableDatesPerHost,
      constants.FETCH_AVAILABLE_DATES_PER_HOST_SUCCESS,
      this.onFetchAvailableDatesPerHostSuccess,
      constants.FETCH_SUGGESTIONS,
      this.onFetchSuggestions,
      constants.FETCH_SUGGESTIONS_SUCCESS,
      this.onFetchSuggestionsSuccess,
      constants.FETCH_SUGGESTIONS_FAIL,
      this.onFetchSuggestionsFail,
    );
  },

  onFetchExperience({ experienceId, lang }) {
    this.experience = {
      lang,
      id: experienceId,
      status: STATUS_PENDING,
      data: null,
    };
    this.redirectUrl = null;
    this.emit('change');
  },

  onFetchExperienceSuccess({ experience, lang }) {
    if (this.experience.id !== experience.id || this.experience.lang !== lang) {
      this.experience = {
        lang,
        ...this.experience,
        status: STATUS_SUCCESS,
        data: experience,
      };
      this.defaultTime = experience.lists.starting_times
        ? experience.lists.starting_times[0]
        : '09:00';
      this.availableDates = {
        experienceId: experience.id,
        status: STATUS_SUCCESS,
        data: experience.availabledates,
      };
      this.emit('change');
    }
  },

  onFetchExperienceRedirect({ redirectUrl }) {
    this.experience = {
      id: null,
      status: STATUS_INITIAL,
      data: null,
    };
    this.redirectUrl = redirectUrl;
    this.emit('change');
  },

  onFetchExperienceNotFound({ id }) {
    this.experience = {
      id,
      status: STATUS_NOT_FOUND,
      data: null,
    };
    this.emit('change');
  },

  onFetchSuggestions({ experienceId }) {
    this.suggestions[experienceId] = {
      status: STATUS_PENDING,
      data: [],
    };
    this.emit('change');
  },

  onFetchSuggestionsSuccess({ experienceId, response }) {
    this.suggestions[experienceId] = {
      status: STATUS_SUCCESS,
      data: response,
    };
    this.emit('change');
  },

  onFetchSuggestionsFail({ experienceId }) {
    this.suggestions[experienceId] = {
      status: STATUS_ERROR,
      data: [],
    };
    this.emit('change');
  },

  onFetchPopularExperiencesSuccess(experiences) {
    this.popularExperiences = experiences;
    this.emit('change');
  },

  onFetchAvailableTimes({ experienceId, date }) {
    if (this.availableTimes.experienceId === experienceId) {
      this.availableTimes = {
        ...this.availableTimes,
        experienceId,
        [date]: {
          status: STATUS_PENDING,
          data: null,
        },
      };
    }

    this.availableTimes = {
      experienceId,
      [date]: {
        status: STATUS_PENDING,
        data: null,
      },
    };

    this.emit('change');
  },

  onFetchAvailableTimesSuccess({ experienceId, date, availableTimes }) {
    for (const [key, value] of Object.entries(availableTimes)) {
      availableTimes[key] = value.map((t) =>
        formatDate(
          parse(`1970-01-01T${t}`, "yyyy-MM-dd'T'HH:mm", new Date()),
          'HH:mm',
        ),
      );
    }
    if (this.availableTimes.experienceId === experienceId) {
      this.availableTimes = {
        ...this.availableTimes,
        experienceId,
        [date]: {
          status: STATUS_SUCCESS,
          data: availableTimes,
        },
      };
      const allTimes = Object.values(availableTimes)
        .reduce((res, x) => {
          return res.concat(x);
        }, [])
        .sort();
      if (!allTimes.includes(this.defaultTime)) {
        this.defaultTime = allTimes[0];
      }
      this.emit('change');
    }
  },

  onFetchAvailableDates({ experienceId }) {
    this.availableDates = {
      experienceId,
      status: STATUS_PENDING,
      data: null,
    };

    this.emit('change');
  },

  onFetchAvailableDatesSuccess({ experienceId, availableDates }) {
    if (this.availableDates.experienceId === experienceId) {
      this.availableDates = {
        experienceId,
        status: STATUS_SUCCESS,
        data: availableDates,
      };
      this.emit('change');
    }
  },

  onFetchAvailableDatesPerHost({ experienceId }) {
    this.availableDatesPerHost = {
      experienceId,
      status: STATUS_PENDING,
      data: null,
    };

    this.emit('change');
  },

  onFetchAvailableDatesPerHostSuccess({ experienceId, availableDates }) {
    if (this.availableDatesPerHost.experienceId === experienceId) {
      this.availableDatesPerHost = {
        experienceId,
        status: STATUS_SUCCESS,
        data: availableDates.availability,
      };
      this.emit('change');
    }
  },

  getSuggestions(experienceId) {
    return this.suggestions[experienceId];
  },

  getExperience() {
    return this.experience;
  },

  getExperienceLang() {
    return this.experience.lang;
  },

  getExperienceData() {
    return this.experience.data;
  },

  getExperienceStatus() {
    return this.experience.status;
  },

  getExperienceIsLoading() {
    return (
      this.experience.status === STATUS_INITIAL ||
      this.experience.status === STATUS_PENDING
    );
  },

  getPopularExperiences() {
    return this.popularExperiences;
  },

  getAvailableTimes() {
    return this.availableTimes;
  },

  getAvailableDates() {
    return this.availableDates;
  },

  getAvailableDatesPerHost() {
    return this.availableDatesPerHost;
  },

  getDefaultTime() {
    return this.defaultTime;
  },

  getRedirectUrl() {
    return this.redirectUrl;
  },
});
